import { useContext, useEffect, useMemo, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { Loader2 } from 'lucide-react';

const CATEGORIES_URL = '/decoupled-api/taxonomy_term/doc_space_category/collection?display=decoupled';

function normalizeCategory(item) {
    const d = item.data ?? item;
    const rawParent = d.parentId;
    const parentId = Array.isArray(rawParent)
        ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
        : (rawParent ? parseInt(rawParent, 10) : 0);
    return { id: d.id, name: d.name, parentId: parentId || 0 };
}

export const DocSpaceCategoryManager = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const ev = (val) =>
        evaluateTemplateValue({ valueToEvaluate: val, globalDataContext, templateContext });

    const rawParentId = ev(props.parentId);
    const parentId = typeof rawParentId === 'number' ? rawParentId : parseInt(rawParentId || '0', 10);
    const rawCategoryIds = ev(props.spaceCategoryIds);
    const spaceCategoryIds = useMemo(
        () => (Array.isArray(rawCategoryIds) ? rawCategoryIds.map(Number).filter(Number.isFinite) : []),
        [rawCategoryIds],
    );

    const isRoot = parentId === 0 || !parentId;

    const [allCategories, setAllCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isRoot || spaceCategoryIds.length === 0) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        fetch(CATEGORIES_URL, { credentials: 'include' })
            .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
            .then((data) => {
                if (cancelled) return;
                const cats = (Array.isArray(data) ? data : [])
                    .filter((item) => item?.data ?? item?.id != null)
                    .map(normalizeCategory);
                setAllCategories(cats);
            })
            .catch(() => { if (!cancelled) setAllCategories([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isRoot, spaceCategoryIds.length]);

    const affiliatedCategories = useMemo(
        () => allCategories.filter((c) => spaceCategoryIds.includes(c.id)),
        [allCategories, spaceCategoryIds],
    );

    if (!isRoot || spaceCategoryIds.length === 0) return null;

    if (loading) {
        return (
            <ActionDependant {...props}>
                <div id="doc-space-categories" className="mb-8">
                    <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Chargement des catégories…</span>
                    </div>
                </div>
            </ActionDependant>
        );
    }

    if (affiliatedCategories.length === 0) return null;

    return (
        <ActionDependant {...props}>
            <div id="doc-space-categories" className="mb-8">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-stone-400 uppercase tracking-widest">Catégories</span>
                    {affiliatedCategories.map((cat) => (
                        <span
                            key={cat.id}
                            className="text-2xs text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full font-medium"
                        >
                            {cat.name}
                        </span>
                    ))}
                </div>
            </div>
        </ActionDependant>
    );
};
